/*
 * Copyright (C) 2025 to Present Applause App Quality, Inc. All rights reserved.
 */

import {
  Component,
  OnInit,
  Inject,
} from '@angular/core';
import { DialogService } from '@ng-falcon/dialog/dialog.service';
import { ArticleService } from '../../services/article.service';
import { RejectArticleModalComponent } from './reject-article-modal.component';

interface Article {
  id: number;
  slug: string;
  title: string;
  content: string;
  user: any;
  updated_at: string;
  rejected_at?: string;
  reject_reason?: string;
  reviewer?: any;
  current_user_permissions?: {
    publish?: boolean;
    reject?: boolean;
  };
}

interface FeedStore {
  feedItems: Article[];
  isLoading: boolean;
  isLastPage: boolean;
  load(): Promise<any>;
  loadMore(): Promise<any>;
}

@Component({
  selector: 'ut-pending-article',
  templateUrl: './pending-article.component.html',
  styleUrls: ['./pending-article.component.scss'],
  standalone: false,
})
export class PendingArticleComponent implements OnInit {
  pendingArticles!: FeedStore;
  rejectedArticles!: FeedStore;
  activeTab: string = 'pending';
  currentUser: any;

  // Mobile alerts
  mobileAlerts: Array<{ type: string; msg: string }> = [];

  constructor(
    private ArticleService: ArticleService,
    @Inject('FeedStore') private FeedStore: any,
    @Inject('AlertService') private AlertService: any,
    @Inject('UserService') private UserService: any,
    private dialogService: DialogService,
  ) {}

  ngOnInit(): void {
    this.UserService.me()
      .then((res: any) => {
        this.currentUser = res?.data ?? null;
      })
      .catch(() => {
        this.currentUser = null;
      });

    const fetchFeed = this.ArticleService.feed.bind(this.ArticleService);

    this.pendingArticles = this.FeedStore.create(fetchFeed, {
      filter: { state: 'review' },
      sort: '-updated_at',
    });

    this.rejectedArticles = this.FeedStore.create(fetchFeed, {
      filter: { state: 'rejected' },
      sort: '-rejected_at',
    });

    this.pendingArticles.load();
    this.rejectedArticles.load();
  }

  onTabChange(tabId: string): void {
    this.activeTab = tabId;

    if (tabId === 'rejected' && this.rejectedArticles.feedItems.length === 0) {
      this.rejectedArticles.load();
    }
  }

  confirmPublish(article: any, source: 'publish' | 'reject'): void {
    this.dialogService.openConfirmCancelDialog({
      title: 'Confirm Publish',
      message: `Are you sure you want to publish "${article.title}"?`,
      confirmLabel: 'Publish',
    }).afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.publishArticle(article.id, source);
      }
    });
  }

  confirmReject(article: any): void {
    this.dialogService.open(RejectArticleModalComponent, { data: article })
      .afterClosed().subscribe(reason => {
        if (reason) {
          this.rejectArticle(article.id, reason);
        }
      });
  }

  publishArticle(articleId: number, source: 'publish' | 'reject'): void {
    this.ArticleService.publish(articleId).then(() => {
      if (source === 'reject') {
        this.removeArticleFromFeed(this.rejectedArticles, articleId);
      } else {
        this.removeArticleFromFeed(this.pendingArticles, articleId);
        if (this.currentUser && this.currentUser.pending_articles_count) {
          this.currentUser.pending_articles_count -= 1;
        }
      }

      this.AlertService.add('Article successfully published', 'success');
    }).catch((error: any) => {
      this.AlertService.add('Failed to publish article', 'danger');
      console.error('Error publishing article:', error);
    });
  }

  rejectArticle(articleId: number, reason: string): void {
    if (!reason || reason.trim() === '') {
      this.AlertService.add('Please enter a reason for rejection', 'danger');
      return;
    }

    this.ArticleService.reject(articleId, reason).then(() => {
      this.removeArticleFromFeed(this.pendingArticles, articleId);
      this.rejectedArticles.load();

      if (this.currentUser && this.currentUser.pending_articles_count) {
        this.currentUser.pending_articles_count -= 1;
      }

      this.AlertService.add('Article rejected', 'success');
    }).catch((error: any) => {
      this.AlertService.add('Failed to reject article', 'danger');
      console.error('Error rejecting article:', error);
    });
  }

  private removeArticleFromFeed(feed: FeedStore, articleId: number): void {
    const index = feed.feedItems.findIndex((article: Article) => article.id === articleId);
    if (index !== -1) {
      feed.feedItems.splice(index, 1);
    }
  }

  closeAlert(index: number): void {
    this.mobileAlerts.splice(index, 1);
  }
}
